// NOMBASE - React Query Client
// Global, cached, instant-feeling data layer.
// Cached results are persisted to localStorage so navigation feels instant
// even on the very first visit to a page (as long as it's in the cache).
import { QueryClient } from '@tanstack/react-query';
import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';

// Sensible defaults:
// - staleTime 60s: most GETs don't change second-to-second; we show cached
//   data immediately and silently revalidate in the background.
// - gcTime (cacheTime) 10min: keeps cache entries around long enough to
//   feel "instant" when jumping between pages.
// - refetchOnWindowFocus false: users don't want a blocking spinner when
//   they flick back to the tab.
// - retry 1: fail fast on genuine errors; react-query still shows the last
//   successful data if it exists.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
    mutations: {
      retry: 0,
    },
  },
});

// localStorage persister — survives refresh. We scope with a version key so
// future schema changes can bust the cache cleanly.
export const PERSISTED_CACHE_KEY = 'nombase_query_cache_v1';

export const persister = createSyncStoragePersister({
  storage: typeof window !== 'undefined' ? window.localStorage : undefined,
  key: PERSISTED_CACHE_KEY,
  throttleTime: 1000,
});

// T42 — clear the persisted React Query cache on identity change
// (Clerk logout or user-id switch). Called from `lib/clerk.jsx`'s
// `ClerkTokenBridge`. Safe to call multiple times.
export function clearPersistedQueryCache() {
  try {
    if (typeof window === 'undefined') return;
    window.localStorage.removeItem(PERSISTED_CACHE_KEY);
  } catch {
    // localStorage may be unavailable (Safari private mode); the in-
    // memory cache was already cleared by the caller via
    // `queryClient.clear()`, so the data leak is mitigated either way.
  }
}

// Helper to invalidate by key prefix — handy for mutations.
export const invalidate = (keyPrefix) =>
  queryClient.invalidateQueries({ queryKey: Array.isArray(keyPrefix) ? keyPrefix : [keyPrefix] });

// Helper to prefetch a query — used by PrefetchLink on hover.
export const prefetch = (key, fn, opts = {}) =>
  queryClient.prefetchQuery({ queryKey: Array.isArray(key) ? key : [key], queryFn: fn, staleTime: 60 * 1000, ...opts });
